import { Page, Text, View, Svg, Path, Document, pdf, StyleSheet } from "@react-pdf/renderer";
import DownloadIcon from "@mui/icons-material/Download";
import { Button, Text as TextUI } from "@/ui";
import { format } from "date-fns";
import { formatUTC } from "@/utils/format";
import { useTheme } from "@mui/material";
import { useState } from "react";
import saveAs from "file-saver";

// Styles
const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#FFFFFF",
    paddingBottom: 50,
  },
  header: {
    padding: 20,
    paddingBottom: 10,
  },
  headerRow: {
    display: "flex",
    flexDirection: "row",
  },
  logoContainer: {
    width: "20%",
  },
  headerInfo: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    width: "80%",
    paddingLeft: 20,
  },
  title: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#FF008D",
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: "#262626",
    marginBottom: 8,
  },
  clientInfo: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginTop: 2,
  },
  clientLabel: {
    fontSize: 9,
    fontWeight: "bold",
    color: "#FF008D",
    textTransform: "uppercase",
  },
  clientValue: {
    fontSize: 9,
    color: "#262626",
  },
  content: {
    paddingHorizontal: 20,
    paddingTop: 10,
  },
  tableContainer: {
    marginBottom: 15,
  },
  organismeTitle: {
    fontSize: 11,
    fontWeight: "bold",
    color: "#262626",
    marginBottom: 6,
    marginTop: 10,
    backgroundColor: "#f8f8f8",
    padding: 8,
  },
  table: {
    borderWidth: 0.5,
    borderColor: "#e0e0e0",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#FF008D",
    minHeight: 22,
  },
  tableHeaderCell: {
    flex: 1,
    padding: 6,
    borderRightWidth: 0.5,
    borderRightColor: "#e0e0e0",
  },
  tableHeaderText: {
    fontSize: 8,
    fontWeight: "bold",
    color: "white",
    textAlign: "center",
    textTransform: "uppercase",
  },
  tableRow: {
    flexDirection: "row",
    borderTopWidth: 0.5,
    borderTopColor: "#e0e0e0",
    minHeight: 20,
  },
  tableRowEven: {
    backgroundColor: "#ffffff",
  },
  tableRowOdd: {
    backgroundColor: "#fdf5f7",
  },
  tableCell: {
    flex: 1,
    padding: 5,
    borderRightWidth: 0.5,
    borderRightColor: "#e0e0e0",
    justifyContent: "center",
  },
  tableCellText: {
    fontSize: 8,
    color: "#262626",
    textAlign: "center",
  },
  tableTotalRow: {
    flexDirection: "row",
    backgroundColor: "#f0f0f0",
    borderTopWidth: 0.5,
    borderTopColor: "#e0e0e0",
    minHeight: 20,
  },
  tableTotalText: {
    fontSize: 8,
    fontWeight: "bold",
    color: "#262626",
    textAlign: "center",
  },
  footer: {
    position: "absolute",
    bottom: 20,
    left: 20,
    right: 20,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderTopWidth: 0.5,
    borderTopColor: "#e0e0e0",
    paddingTop: 10,
  },
  footerText: {
    fontSize: 8,
    color: "#666666",
  },
});

// Format date function — les dates de soins (date_debut/date_fin) sont des dates calendaires
// pures (pas de vraie notion d'heure) : on utilise formatUTC pour ne pas dépendre du fuseau du navigateur.
const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return formatUTC(dateStr, "dd/MM/yyyy");
};

// Header component (fixed on each page)
const Header = ({ period, currentUser }) => (
  <View style={styles.header} fixed>
    <View style={styles.headerRow}>
      <View style={styles.logoContainer}>
        <Svg width="80" viewBox="0 0 560.8 280.4">
          <Path
            fill="#FF008D"
            d="M130.9,104.5L66,175l61.9,70.2c10.5,11.9,10.1,30.9-0.8,42.4c-5.4,5.7-12.5,8.5-19.5,8.4 c-7.1-0.1-14.1-3.2-19.4-9.1L7.7,195.5c-10.4-11.8-10.2-30.4,0.4-42l84-91.4c10.8-11.7,28.2-11.7,38.9,0 C141.7,73.8,141.7,92.8,130.9,104.5z M387,110.7c-0.4,0.3-44.1,27.7-99,37.5c-55.2,9.7-108.8-1.5-109.3-1.6 c-14.9-3.2-29.3,7.3-32.4,23.5c-3,16.2,6.6,32,21.5,35.2c1.7,0.4,28.9,6.2,66,6.7c19,0.3,40.7-0.8,62.8-4.7 c65.5-11.5,116-43.5,118.1-44.9c13.1-8.4,17.5-26.7,9.9-41C417,107.1,400.1,102.4,387,110.7z M560.8,75.9 c-2.4-7.6-7.5-13.8-14.1-17.2l-107-55.7C426-4.2,409.6,2.1,403,17.1c-6.6,14.9-0.8,32.8,12.9,39.9l81.9,42.6l-39.5,86.8 c-6.8,14.8-1.2,32.8,12.4,40.1c3.8,2,7.8,3.1,11.8,3.1c10.3,0.2,20.2-6,25.1-16.7l51.9-114.1C562.7,91.7,563.2,83.4,560.8,75.9z"
          />
        </Svg>
      </View>
      <View style={styles.headerInfo}>
        <Text style={styles.title}>Cofidoc</Text>
        <Text style={styles.subtitle}>
          Paiements reçus du mois de {format(new Date(Number(period)), "MMMM yyyy")}
        </Text>
        <View style={styles.clientInfo}>
          <Text style={styles.clientLabel}>Client : </Text>
          <Text style={styles.clientValue}>
            {currentUser?.prenom || currentUser?.firstname || ""} {currentUser?.nom || currentUser?.lastname || ""}
          </Text>
        </View>
        <View style={styles.clientInfo}>
          <Text style={styles.clientLabel}>Identifiant : </Text>
          <Text style={styles.clientValue}>{currentUser?.identifiant || "-"}</Text>
        </View>
      </View>
    </View>
  </View>
);

// Footer component (fixed on each page)
const Footer = () => (
  <View style={styles.footer} fixed>
    <Text style={styles.footerText}>Cofidoc</Text>
    <Text style={styles.footerText} render={({ pageNumber, totalPages }) => `Page ${pageNumber} / ${totalPages}`} />
    <Text style={styles.footerText}>
      Le {format(new Date(), "dd/MM/yyyy")} à {format(new Date(), "HH:mm")}
    </Text>
  </View>
);

// Table Header component
const TableHeader = () => (
  <View style={styles.tableHeader} wrap={false}>
    <View style={styles.tableHeaderCell}>
      <Text style={styles.tableHeaderText}>N° Facture</Text>
    </View>
    <View style={styles.tableHeaderCell}>
      <Text style={styles.tableHeaderText}>Patient</Text>
    </View>
    <View style={styles.tableHeaderCell}>
      <Text style={styles.tableHeaderText}>Date</Text>
    </View>
    <View style={styles.tableHeaderCell}>
      <Text style={styles.tableHeaderText}>Facturé</Text>
    </View>
    <View style={[styles.tableHeaderCell, { borderRightWidth: 0 }]}>
      <Text style={styles.tableHeaderText}>Payé</Text>
    </View>
  </View>
);

// Table Row component
const TableRow = ({ detail, index }) => (
  <View style={[styles.tableRow, index % 2 === 0 ? styles.tableRowEven : styles.tableRowOdd]} wrap={false}>
    <View style={styles.tableCell}>
      <Text style={styles.tableCellText}>{detail.num_facture || "-"}</Text>
    </View>
    <View style={styles.tableCell}>
      <Text style={styles.tableCellText}>{detail.nom_patient || detail.factures?.nom_patient || "-"}</Text>
    </View>
    <View style={styles.tableCell}>
      <Text style={styles.tableCellText}>
        {formatDate(detail.date_debut)} - {formatDate(detail.date_fin)}
      </Text>
    </View>
    <View style={styles.tableCell}>
      <Text style={styles.tableCellText}>{parseFloat(detail.montant_facture || 0).toFixed(2)} €</Text>
    </View>
    <View style={[styles.tableCell, { borderRightWidth: 0 }]}>
      <Text style={styles.tableCellText}>{parseFloat(detail.montant_paye || 0).toFixed(2)} €</Text>
    </View>
  </View>
);

// Table Total Row component
const TableTotalRow = ({ details }) => {
  const totalFacture = details.reduce((sum, d) => sum + parseFloat(d.montant_facture || 0), 0);
  const totalPaye = details.reduce((sum, d) => sum + parseFloat(d.montant_paye || 0), 0);

  return (
    <View style={styles.tableTotalRow} wrap={false}>
      <View style={styles.tableCell}>
        <Text style={styles.tableTotalText}>Total</Text>
      </View>
      <View style={styles.tableCell}>
        <Text style={styles.tableTotalText}>-</Text>
      </View>
      <View style={styles.tableCell}>
        <Text style={styles.tableTotalText}>-</Text>
      </View>
      <View style={styles.tableCell}>
        <Text style={styles.tableTotalText}>{totalFacture.toFixed(2)} €</Text>
      </View>
      <View style={[styles.tableCell, { borderRightWidth: 0 }]}>
        <Text style={styles.tableTotalText}>{totalPaye.toFixed(2)} €</Text>
      </View>
    </View>
  );
};

// Organisme Title component (wrap={false} to keep title with first rows)
const OrganismeTitle = ({ nom }) => (
  <View style={styles.organismeTitle} wrap={false}>
    <Text>Organisme : {nom}</Text>
  </View>
);

// Refund Table component - permet le wrap pour les tableaux longs
const RefundTable = ({ refund }) => (
  <View style={styles.tableContainer}>
    <OrganismeTitle nom={refund.nom_organisme} />
    <View style={styles.table}>
      <TableHeader />
      {refund.details_remboursements?.map((detail, index) => (
        <TableRow key={detail.detail_id || index} detail={detail} index={index} />
      ))}
      <TableTotalRow details={refund.details_remboursements || []} />
    </View>
  </View>
);

// Main PDF Document
export const RefundPDF = ({ data, period, currentUser }) => {
  return (
    <Document title="Remboursements">
      <Page size="A4" style={styles.page}>
        <Header period={period} currentUser={currentUser} />
        <View style={styles.content}>
          {data?.map((refund, index) => (
            <RefundTable key={refund.remboursement_id || index} refund={refund} />
          ))}
        </View>
        <Footer />
      </Page>
    </Document>
  );
};

const DownloadRefundPDF = ({ data, period, currentUser }) => {
  const [loading, setLoading] = useState(false);
  const theme = useTheme();

  async function makePdf() {
    setLoading(true);
    try {
      const blob = await pdf(<RefundPDF key={period} data={data} period={period} currentUser={currentUser} />).toBlob();
      saveAs(blob, `remboursements-${format(new Date(Number(period)), "MM-yyyy")}.pdf`);
    } catch (error) {
      console.error("Erreur lors de la génération du PDF:", error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Button disabled={loading} onClick={makePdf}>
      <DownloadIcon fontSize="small" style={{ color: theme.colors.primary as any }} />
      <TextUI m={0} style={{ marginLeft: "4px" }}>
        {loading ? "Chargement du document..." : "Télécharger le PDF"}
      </TextUI>
    </Button>
  );
};

export default DownloadRefundPDF;
